import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subscription, take, firstValueFrom, finalize } from "rxjs";
import { DbService } from "./db.service";

import { HttpClient } from "@angular/common/http";
import { DataSynchronizerService } from "./data-synchronization.service";
import { ToastService } from "./toast.service";
import Dexie, { Table } from "dexie";
import { MatDialog } from "@angular/material/dialog";
import { LoadingDialogComponent } from "../dialogs/loading-dialog/loading-dialog.component";
import { AppDb } from 'fakturnia-shared'
import { DbSynchronization } from "fakturnia-shared";
import { APIResponse } from "fakturnia-shared";
import { API_HOST, settings } from "environments/environment";
import { CONST } from "shared/const";
import { AuthService } from "./auth.service";
import { ConfirmDialogComponent } from "shared/dialogs/confirm-dialog/confirm-dialog.component";

@Injectable({
    providedIn: 'root'
})
export class SynchronizationService implements OnDestroy {

    private _subscriptions: Subscription[] = [];


    private _synchronizationStatus = new BehaviorSubject<any>({ isSynchronizing: false, hasSuccessfulSynchronize: false, changeOccured: false, currentPage: 0, allPages: 0 });
    currentSynchronizationStatus = this._synchronizationStatus.asObservable();
    private _tables = [
        'documents',
        'clients',
        'products',
        'activityLogs',
        // 'ksefInvoices', // DEPRECATED: Faktury z KSeF są teraz w documents (Unified Document Store)
        'notifications'
    ]

    private _db: AppDb

    // Get them on db load, then update variables
    // It has to be done like this, to update multiple open carts in memory data
    private _lastSyncAt: string | null | any = null
    // Must be declared as date
    private _migrationVersion: string = CONST.DEFAULT_MIGRATION_VERSION

    constructor(
        private _dbService: DbService,
        private _httpClient: HttpClient,
        private _dataSynchronizerService: DataSynchronizerService,
        private _toastService: ToastService,
        private _dialog: MatDialog
    ) {
        this._subscriptions.push(
            this._dbService.getDatabase().subscribe({
                next: async (data) => {
                    this._db = data
                    // Set last sync as null
                    // If last sync is null it will try to get default value or value from database
                    this._lastSyncAt = null

                    // Get last sync on every database change
                    // Reconnect or login
                    if (this._db == null) return

                    await this.getLastSyncFromDatabase()
                }
            }))

            
    }

    // If user is logged out clean last sync at
    public resetLastSync() {
        this._lastSyncAt = null
        this._migrationVersion = CONST.DEFAULT_MIGRATION_VERSION
    }

    async synchronize(from: string = 'not-provided') {

        let errorMessage: string | null = null
        if (this._synchronizationStatus.value.isSynchronizing) {
            console.warn("[Synchronization Service]: Sync (from " + from + ") is in progress. Can't sync right now.");
            return;
        }

        this._synchronizationStatus.next({ error: null, isSynchronizing: true, hasSuccessfulSynchronize: false, data: null, changeOccured: false, currentPage: 0, allPages: 0 })

        const toPaginate: any = []
        this._tables.forEach(table => {
            toPaginate.push({ table: table, allPages: 0, currentPage: 0 })
        })

        this._synchronizationStatus.next({ error: null, isSynchronizing: true, hasSuccessfulSynchronize: false, data: null, changeOccured: false, currentPage: 0, allPages: 0 })

        if (this._db == null) {
            console.error("[Synchronization Service]: Database is not loaded yet.");
            this._synchronizationStatus.next({ error: null, isSynchronizing: false, hasSuccessfulSynchronize: false, data: null, changeOccured: false, currentPage: 0, allPages: 0 })
            return
        }

        const dataToPush: { table: string, data: any, syncAt: string | null }[] = []

        // Get latest sync to check if it is a first synchronization
        const { version } = await this._getLastSyncDate()
        const syncSub = this._httpClient.post(
            API_HOST + 'account/synchronize',
            await this._getRequestBody(1, toPaginate))
            .pipe(take(1))
            .pipe(finalize(() => {
                this._synchronizationStatus.next({ error: errorMessage, isSynchronizing: false, savingStatus: null, hasSuccessfulSynchronize: false, changeOccured: false })
            }))
            .subscribe({
                next: async (response: APIResponse) => {

                    if (response.success == false) {
                        this._toastService.warning(response.message)
                        this._processError(null)
                        return
                    }

                    // Stores response data
                    let data = response.data

                    let changeOccured = false;

                    // Stores biggest page
                    let biggestPage = 0;


                    // If its not a first version and version is diffrent
                    if (version != CONST.DEFAULT_MIGRATION_VERSION && version != response.data.version) {
                        console.warn(['[Synchronization Service]: Application is updating'])
                        this._showAppUpdateDialog()

                        this._hardReset()
                        syncSub.unsubscribe()
                        return
                    }

                    this._setMigrationVersion(response.data.version)
                   
                    if (this._db == null) { return; }
                    if (response.data != null) {

                        toPaginate.forEach(paginator => {
                            if (response.data[paginator.table].totalPages > biggestPage) { biggestPage = response.data[paginator.table].totalPages }
                            paginator.totalPages = response.data[paginator.table].totalPages;
                            paginator.currentPage = response.data[paginator.table].currentPage;
                        })

                        this._synchronizationStatus.next({ error: null, isSynchronizing: true, hasSuccessfulSynchronize: false, changeOccured: false, currentPage: 1, allPages: biggestPage })

                        changeOccured = true;
                        console.log(`[Synchronization Service]: Detected ${biggestPage} pages to import.`)
                        for (let i = 1; i <= biggestPage; i++) {

                            let hasError = false;
                            let err = null;
                            if (i > 1) {
                                await this._getNewSyncPage(i, toPaginate).then((nextResponse: APIResponse) => {

                                    if (nextResponse.success == false) {
                                        this._toastService.warning(nextResponse.message)
                                        this._processError(null)
                                        return
                                    }
                                    response.data = nextResponse.data
                                    // Upewnij się, że lokalna referencja danych jest aktualna dla kolejnych stron
                                    data = response.data
                                })
                                    .catch((e) => {
                                        hasError = true;
                                        err = e;
                                    })
                            }

                            if (hasError) {
                                this._synchronizationStatus.next({ error: err, isSynchronizing: false, hasSuccessfulSynchronize: false, changeOccured: false, currentPage: i, allPages: biggestPage })
                                return;
                            }


                            for (let tI = 0; tI < toPaginate.length; tI++) {
                                const table = toPaginate[tI].table;
                                if (typeof data[table] != 'undefined') {
                                  // Log: start przetwarzania każdej tabeli (nawet gdy brak elementów)
                                  const itemsCount = Array.isArray(data[table].items) ? data[table].items.length : 0;
                                  console.log(`[SynchronizationService]: Processing ${table} (page ${i}) - items: ${itemsCount}`);

                                  if (itemsCount > 0) {
                
                                  // Save data to database
                                  let temp:any = [];
                
                                  data[table].items = data[table].items
                
                                  for (let row of data[table].items) {
                                    temp.push(row);
                                  }
                
                                  //Take first part and check if there is more pages
                                  if (temp.length > 0) {
                                    this._synchronizationStatus.next({ error: errorMessage, isSynchronizing: true, savingStatus: null, hasSuccessfulSynchronize: false, lastSyncAt: this._lastSyncAt, changeOccured: false, currentPage: i, allPages: biggestPage })
                
                                    // Update data in database
                                    try {
                                     
                                    
                                      dataToPush.push({ table: table, data: temp, syncAt: this._lastSyncAt })
                
                                    }
                                    catch (ex:any) {
                                      if (!ex) console.warn(`[SynchronizationService]: Error thrown. No details.`)
                                      else console.warn(`${ex.message}`)
                                      syncSub.unsubscribe()
                                      return;
                                    }
                                  }
                                  }
                                }
                            }
                        }
                    }

                    const saveResult = await this.saveDataToDatabase(dataToPush)
                    if (saveResult != true) {

                        this._showDatabseErrorDialog()
                        setTimeout(() => {
                            errorMessage = '[Synchronization Service]: Couldnt save data.'
                            this._synchronizationStatus.next({ error: errorMessage, isSynchronizing: false, savingStatus: null, hasSuccessfulSynchronize: false, changeOccured: false, lastSyncAt: this._lastSyncAt })
                        }, 100)
                        syncSub.unsubscribe()
                        return
                    }

                    // If there is more than 1 page reload app
                    // But remember to set last sync date
                    if (biggestPage > 1) {
                        await this._setLastSyncDate(data.lastSync)
                        setTimeout(() => {
                        window.location.reload()
                        }, 100)
                    }
                    // else update inmemory data
                    else {
                        for (let row of dataToPush) {
                        this._dataSynchronizerService.next(row.table, row.data, row.syncAt)
                        }
                    }

                    await this._setLastSyncDate(response.data.lastSyncAt)
                    this._synchronizationStatus.next({ error: null, isSynchronizing: true, hasSuccessfulSynchronize: true, changeOccured: changeOccured })
                    let t2 = new Date().getTime();
                    // console.warn(`Sync time: ${t2 - t1}ms (${((t2 - t1) / 1000).toFixed(4)} sec)`);

                    // setTimeout(() => {
                    this._synchronizationStatus.next({ error: null, isSynchronizing: false, hasSuccessfulSynchronize: false, changeOccured: false })
                    // }, 100)


                },
                error: (err) => {
                    this._processError(err);
                }
            }
            )
    }

    // Do not add here foreach loop (it prevents error handler)
    async saveDataToDatabase(data) {

        try {
            if (data.length == 0) return true
            const tables = new Set(data.map(x => x.table))
            const dexieTables: Table<any>[] = Array.from(tables).map((x: string) => this._db[x])

            const tableData: TableDataObject = data.reduce((acc, row) => {
                acc[row.table] = acc[row.table] || [];
                acc[row.table] = acc[row.table].concat(row.data);
                return acc;
            }, {});

            await this._db.transaction("rw", dexieTables, async tx => {
                const keys = Object.keys(tableData)

                for (let x = 0; x < keys.length; x++) {
                    const table = keys[x]

                    await this._db[table].bulkPut(tableData[table]).catch(Dexie.BulkError, function (e) {
                        console.warn(`[SynchronizationService]: Pushing ${e.failures.length} failed to ${table} table.`)
                    }).catch((e) => {
                        console.warn('[SynchronizationService]: Invalid data when bulk put to table. ', tableData[table])
                        console.error(`[SynchronizationService] Data not fully saved in ${table} table`)
                    })
                    this._synchronizationStatus.next({ error: null, isSynchronizing: true, savingStatus: { success: x + 1, all: keys.length }, hasSuccessfulSynchronize: true, changeOccured: this._synchronizationStatus.value.changeOccured })

                    console.log(`[SynchronizationService]: Pushing ${tableData[table].length} rows to ${table} table.`)
                }
            })
            return true
        }
        catch (error) {
            console.error("Error saving data to database:", error);
            return false; // Reject the Promise on error
        }
    }

    private async _hardReset() {

        try {
            const dbs = await Dexie.getDatabaseNames()

            dbs.forEach(db => {
                const deleteRequest = indexedDB.deleteDatabase(db);

                deleteRequest.onsuccess = () => {
                    console.log(`[SynchronizationService]: Updating application databases: ${db}`);
                };

                deleteRequest.onerror = (error) => {
                    console.error(`[SynchronizationService]: Error deleting database ${db}:`, error);
                };
            })
        } catch (ex: any) {
            console.log([`[SynchronizationService]: ${ex.message}`])
        }


        setTimeout(() => {
            window.location.reload()
        }, 3000)

        return;
    }

 
    private async _setLastSyncDate(lastSync) {
        if (!this._db) return;
        console.warn(`[SynchronizationService]: Saving last synchronization date time: ${this._db.name}-lastSync ${lastSync}`);
        await this._db.synchronizations.where("id").equals(1).modify({ lastSync: lastSync });
    }

    private async _getRequestBody(i, toPaginate) {

        this._lastSyncAt = (await this._getLastSyncDate()).lastSync
        let request = {
            lastSyncAt: this._lastSyncAt,
            appVersion: settings.version,
            deviceId: localStorage.getItem('device_id')
        }

        toPaginate.forEach(element => {

            element.currentPage = i;
            if (element.currentPage > element.totalPages) {
                element.currentPage = 0;
            }

            request[element.table] = {
                page: element.currentPage
            }
        })

        return request
    }

    private async _setMigrationVersion(version) {
        this._migrationVersion = version
        console.warn(`[Synchronization Service]: Saving migration version: ${version}`);
        await this._db.synchronizations.where("id").equals(1).modify({ version: version });
    }

    private async _getLastSyncDate() {
        // Get last sync date from user database.
        if (!this._db) return { lastSync: CONST.DEFAULT_SYNC_TIME, version: CONST.DEFAULT_MIGRATION_VERSION }
        let sync = await this._db.synchronizations.get({ id: 1 })

        // If not exists or lastSync key changed try to bulk add record
        if (sync == null || typeof sync.lastSync == 'undefined') {
            sync = new DbSynchronization(1, CONST.DEFAULT_SYNC_TIME, CONST.DEFAULT_MIGRATION_VERSION);
            await this._db.synchronizations.put(sync);
        }

        console.warn(`[Synchronization Service]: Getting last synchronization date time: ${this._db.name}-lastSync ${sync.lastSync}`);

        if(sync.lastSync === '0000-00-00 00:00:00.000000') sync.lastSync = '0000-00-00 00:00:00'
        return {
            lastSync: sync.lastSync,
            version: sync.version
        };
    }

    async getLastSyncFromDatabase() {

        if (!this._db) return
        const { lastSync, version } = await this._getLastSyncDate()
    
        if (lastSync == null || version == null) return false
        this._lastSyncAt = lastSync
        this._migrationVersion = version
    
        if (this._lastSyncAt == null) this._lastSyncAt = CONST.DEFAULT_SYNC_TIME
        if (this._migrationVersion == null) this._migrationVersion = CONST.DEFAULT_MIGRATION_VERSION
    
        return true
      }

    private async _getNewSyncPage(i, toPaginate) {
        return firstValueFrom(
            this._httpClient.post(
                API_HOST + 'account/synchronize',
                await this._getRequestBody(i, toPaginate)
            )
        )
    }

    private _processError(err) {
        console.warn("[Synchronization Service] Failed to synchronize.")
        this._synchronizationStatus.next({ error: err, isSynchronizing: false, hasSuccessfulSynchronize: false, changeOccured: false })
    }

    private _showAppUpdateDialog() {

        this._dialog.open(LoadingDialogComponent, {
            data: {
                title: 'Aktualizacja aplikacji',
                description: 'Trwa aktualizacja aplikacji. Proszę czekać.'
            },
            disableClose: true
        })

    }

    private _showDatabseErrorDialog() {

        const dialog = this._dialog.open(ConfirmDialogComponent, {
            data: {
                title: '[10001] Błąd operacji',
                message: 'Wystąpił błąd podczas operacji. Jeżeli problem się pojawia, skontaktuj się z administratorem.',
                type: 'warning',
                confirmText: 'Spróbuj ponownie'
            },
            disableClose: true
        })

        dialog.afterClosed().subscribe({
            next: (data) => {
              if (data?.action == true) {
                this._hardReset()
              }
            }
        })

    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(sub => sub.unsubscribe())
    }
}



export interface TableDataObject {
    [key: string]: any
}

